import {
  actionItemByIdOptions,
  useCommentOnActionItemMutation,
  useResolveActionItemMutation,
} from '@cartwheel/api/v1'
import { Button } from '@cartwheel/components'
import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { ActionItemModal } from './action-item-modal'

interface JourneyActionItemButtonProps {
  actionItemId: string
  calloutContainerId?: string
  source: 'homepage' | 'student_profile'
  onResolved?: () => void
  onResolveError?: () => void
}

export default function JourneyActionItemButton({
  actionItemId,
  calloutContainerId,
  source,
  onResolved,
  onResolveError,
}: JourneyActionItemButtonProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [isHidden, setIsHidden] = useState(false)

  const {
    data: actionItem,
    isLoading,
    error,
  } = useQuery({
    ...actionItemByIdOptions({
      path: { id: actionItemId },
      query: { source },
    }),
    enabled: isOpen,
  })

  const resolveMutation = useResolveActionItemMutation()
  const addCommentMutation = useCommentOnActionItemMutation()

  if (isHidden) {
    return null
  }

  const hideCallout = () => {
    setIsHidden(true)
    onResolved?.()

    if (!calloutContainerId) return

    const calloutElement = document.getElementById(calloutContainerId)
    if (calloutElement) {
      calloutElement.style.display = 'none'
    }
  }

  const showCallout = () => {
    setIsHidden(false)
    onResolveError?.()

    if (!calloutContainerId) return

    const calloutElement = document.getElementById(calloutContainerId)
    if (calloutElement) {
      calloutElement.style.display = ''
    }
  }

  const refreshRecentActivity = () => {
    const frame = document.querySelector<HTMLElement & { reload: () => void }>(
      'turbo-frame#client-events-index'
    )
    frame?.reload()
  }

  const handleMarkCompleted = (note: string) => {
    hideCallout()

    resolveMutation.mutate(
      {
        path: { id: actionItemId },
        body: note ? { note } : undefined,
      },
      {
        onSuccess: () => {
          setIsOpen(false)
          refreshRecentActivity()
        },
        onError: () => {
          showCallout()
        },
      }
    )
  }

  const handleSendNote = (note: string) => {
    addCommentMutation.mutate(
      {
        path: { id: actionItemId },
        body: { note },
      },
      {
        onSuccess: () => {
          setIsOpen(false)
          refreshRecentActivity()
        },
      }
    )
  }

  return (
    <>
      <Button variant="secondary" onClick={() => setIsOpen(true)}>
        Take action
      </Button>

      <ActionItemModal
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        actionItem={actionItem || null}
        onMarkCompleted={handleMarkCompleted}
        onSendNote={handleSendNote}
        isPending={resolveMutation.isPending || addCommentMutation.isPending}
        isLoading={isLoading}
        errorMessage={error ? error.message || 'Unable to load action item details.' : null}
      />
    </>
  )
}
